import { useId, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { motion } from 'framer-motion';
import { ChevronDownIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as SolidStarIcon } from '@heroicons/react/24/solid';

const SubMenus = ({ data, isOpen, onToggle, favoriteMenu, isFavorite, isSaving }) => {
  const { pathname } = useLocation();
  const [openThirdId, setOpenThirdId] = useState(null);
  const menuId = useId();

  const normalizePath = (path) => (path ? path.replace(/\/+$/, '').toLowerCase() : '');
  const currentPath = normalizePath(pathname);

  const isPathActive = (path) => {
    const normalized = normalizePath(path);
    if (!normalized) return false;
    return currentPath === normalized || currentPath.startsWith(normalized + '/');
  };

  const anySubMenuActive = (data.subs || []).some((subMenu) => {
    if (isPathActive(subMenu.to)) return true;
    return (subMenu.subs || []).some((nestedMenu) => isPathActive(nestedMenu.to));
  });

  const renderFavoriteButton = (menu) => {
    if (!favoriteMenu || !menu.id) return null;

    const favorited = Boolean(isFavorite?.(menu.id));
    const label = favorited
      ? 'Hapus ' + menu.label + ' dari favorit'
      : 'Tambahkan ' + menu.label + ' ke favorit';

    return (
      <button
        type='button'
        aria-label={label}
        title={label}
        disabled={isSaving}
        onClick={() => favoriteMenu(menu.id, !favorited)}
        className={[
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-1 disabled:cursor-wait disabled:opacity-50',
          'md:opacity-0 md:group-hover/menu-item:opacity-100 md:group-focus-within/menu-item:opacity-100',
          favorited
            ? 'text-amber-600 hover:bg-amber-50 hover:text-amber-700'
            : 'text-gray-400 hover:bg-amber-50 hover:text-amber-600',
        ].join(' ')}
        aria-pressed={favorited}
      >
        {favorited ? (
          <SolidStarIcon aria-hidden='true' className='h-4 w-4' />
        ) : (
          <StarIcon aria-hidden='true' className='h-4 w-4' />
        )}
      </button>
    );
  };

  return (
    <li className='w-full min-w-0'>
      <button
        type='button'
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={onToggle}
        className={[
          'link button button--text flex w-full min-w-0 items-center justify-between gap-2! px-2 text-left',
          anySubMenuActive ? 'active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className='flex min-w-0 flex-1 items-center gap-2!'>
          <span className='shrink-0 text-sm text-gray-500'>{data.icon}</span>
          <span
            className={[
              'min-w-0 flex-1 truncate text-xs capitalize tracking-wide',
              anySubMenuActive ? 'text-primary' : 'text-gray-900',
            ].join(' ')}
          >
            {data.label}
          </span>
        </span>
        <span className='ml-auto flex shrink-0 items-center gap-3'>
          {data.unreadTotal > 0 && (
            <span className='flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1.5 text-[9px] font-bold text-white ring-2 ring-primary/30'>
              {data.unreadTotal > 99 ? '99+' : data.unreadTotal}
            </span>
          )}
          <ChevronDownIcon
            aria-hidden='true'
            className={['h-4 w-4 shrink-0 transition-transform', isOpen ? 'rotate-180' : '']
              .filter(Boolean)
              .join(' ')}
          />
        </span>
      </button>
      <motion.ul
        id={menuId}
        aria-hidden={!isOpen}
        inert={!isOpen}
        initial={false}
        animate={isOpen ? { height: 'auto' } : { height: 0 }}
        className='ml-10 flex h-0 flex-col gap-y-0 overflow-hidden border-l border-slate-200 pl-4 text-[0.8rem] font-normal'
      >
        {data.subs?.map((menu, index) => {
          const menuActive = isPathActive(menu.to);
          const menuUnread =
            menu.id === 'message-customer'
              ? Number(menu.unreadCustomer || 0)
              : menu.id === 'message-guest'
                ? Number(menu.unreadGuest || 0)
                : 0;
          const nestedMenuId = menuId + '-' + (menu.id || index);
          const nestedMenuOpen = openThirdId === menu.id || menuActive;

          return (
            <li key={menu.id || menu.to || menu.label}>
              {!menu.subs?.length ? (
                <div className='group/menu-item flex min-w-0 items-center gap-px'>
                  <NavLink
                    to={menu.to}
                    title={menu.label}
                    className={[
                      'link button button--text flex min-w-0 flex-1 items-center justify-between gap-2! px-2 py-2! text-left capitalize',
                      menuActive ? 'bg-primary-light font-semibold text-primary' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <span
                      className={[
                        'min-w-0 flex-1 truncate text-xs',
                        menuActive ? 'font-semibold text-primary' : 'text-gray-600',
                      ].join(' ')}
                    >
                      {menu.label}
                    </span>
                    {menuUnread > 0 && (
                      <span className='flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1.5 text-[9px] font-bold text-white ring-2 ring-primary/30'>
                        {menuUnread > 99 ? '99+' : menuUnread}
                      </span>
                    )}
                  </NavLink>
                  {renderFavoriteButton(menu)}
                </div>
              ) : (
                <>
                  <button
                    type='button'
                    aria-expanded={nestedMenuOpen}
                    aria-controls={nestedMenuId}
                    onClick={() =>
                      setOpenThirdId((previous) => (previous === menu.id ? null : menu.id))
                    }
                    className={[
                      'link flex w-full min-w-0 items-center justify-between gap-2! px-2 text-left capitalize',
                      menuActive ? 'font-semibold text-primary' : 'text-gray-700',
                    ].join(' ')}
                  >
                    <span className='min-w-0 flex-1 truncate'>{menu.label}</span>
                    <ChevronDownIcon
                      aria-hidden='true'
                      className={[
                        'h-4 w-4 transition-transform',
                        nestedMenuOpen ? 'rotate-180' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    />
                  </button>
                  <motion.ul
                    id={nestedMenuId}
                    aria-hidden={!nestedMenuOpen}
                    inert={!nestedMenuOpen}
                    initial={false}
                    animate={nestedMenuOpen ? { height: 'auto' } : { height: 0 }}
                    className='flex h-0 flex-col overflow-hidden pl-4 text-[0.8rem] font-normal'
                  >
                    {menu.subs.map((third) => {
                      const thirdActive = isPathActive(third.to);

                      return (
                        <li key={third.id || third.to || third.label}>
                          <NavLink
                            to={third.to}
                            className={`link min-w-0 truncate px-2 text-left !bg-transparent capitalize ${
                              thirdActive ? 'font-semibold text-primary' : ''
                            }`}
                          >
                            {third.label}
                          </NavLink>
                        </li>
                      );
                    })}
                  </motion.ul>
                </>
              )}
            </li>
          );
        })}
      </motion.ul>
    </li>
  );
};

export default SubMenus;
